import { format, addWeeks, addMonths } from 'date-fns';
import { CalendarIcon, BellOff, Bell } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { usePaymentSnooze } from '@/hooks/usePaymentSnooze';
import type { IPaymentSnoozePopoverProps } from '@/@types/insurance';

const PRESETS = [
  { label: '1 Week', getDate: () => addWeeks(new Date(), 1) },
  { label: '2 Weeks', getDate: () => addWeeks(new Date(), 2) },
  { label: '1 Month', getDate: () => addMonths(new Date(), 1) },
];

function formatSnoozeDate(ts: number): string {
  return format(new Date(ts), 'dd MMM yyyy');
}

export function PaymentSnoozePopover({ trackingId, currentSnooze }: IPaymentSnoozePopoverProps) {
  const {
    open,
    selectedDate,
    note,
    isSaving,
    canSave,
    setSelectedDate,
    setNote,
    handleOpenChange,
    handleUnsnooze,
    handleSave,
  } = usePaymentSnooze(trackingId);

  const isSnoozed = !!currentSnooze;

  function handlePreset(getDate: () => Date) {
    setSelectedDate(getDate());
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={e => e.stopPropagation()}
          aria-label={
            isSnoozed
              ? `Snoozed until ${formatSnoozeDate(currentSnooze.snoozedUntil)}`
              : 'Snooze policy'
          }
          className={cn(
            'flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            isSnoozed
              ? 'bg-amber-100 text-amber-700 hover:bg-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:hover:bg-amber-900/50'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted'
          )}
        >
          {isSnoozed ? (
            <>
              <BellOff className="h-3.5 w-3.5" />
              <span>Until {formatSnoozeDate(currentSnooze.snoozedUntil)}</span>
            </>
          ) : (
            <>
              <Bell className="h-3.5 w-3.5" />
              <span>Snooze</span>
            </>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent className="w-80 p-4" align="end" onClick={e => e.stopPropagation()}>
        {isSnoozed ? (
          <UnsnoozeContent
            snoozedUntil={currentSnooze.snoozedUntil}
            snoozeNote={currentSnooze.snoozeNote}
            onUnsnooze={() => void handleUnsnooze()}
            onCancel={() => handleOpenChange(false)}
            isSaving={isSaving}
          />
        ) : (
          <SnoozeContent
            selectedDate={selectedDate}
            note={note}
            onPreset={handlePreset}
            onDateSelect={setSelectedDate}
            onNoteChange={setNote}
            onSave={() => void handleSave()}
            onCancel={() => handleOpenChange(false)}
            canSave={canSave}
          />
        )}
      </PopoverContent>
    </Popover>
  );
}

interface IUnsnoozeContentProps {
  // LOCAL_TYPE_JUSTIFIED: Private sub-component props used only in this file — not shared across domain
  snoozedUntil: number;
  snoozeNote?: string;
  onUnsnooze: () => void;
  onCancel: () => void;
  isSaving: boolean;
}

function UnsnoozeContent({
  snoozedUntil,
  snoozeNote,
  onUnsnooze,
  onCancel,
  isSaving,
}: IUnsnoozeContentProps) {
  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs font-semibold text-foreground uppercase tracking-wider">Snoozed</p>
      <div className="text-sm text-foreground">
        <span className="text-muted-foreground">Until: </span>
        {formatSnoozeDate(snoozedUntil)}
      </div>
      {snoozeNote && (
        <div className="text-sm">
          <span className="text-muted-foreground">Note: </span>
          <span className="text-foreground">{snoozeNote}</span>
        </div>
      )}
      <div className="flex justify-end gap-2 pt-1">
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          variant="default"
          size="sm"
          onClick={onUnsnooze}
          disabled={isSaving}
          className="bg-amber-600 hover:bg-amber-700 text-white"
        >
          <BellOff className="h-3.5 w-3.5 mr-1" />
          Unsnooze
        </Button>
      </div>
    </div>
  );
}

interface ISnoozeContentProps {
  // LOCAL_TYPE_JUSTIFIED: Private sub-component props used only in this file — not shared across domain
  selectedDate: Date | undefined;
  note: string;
  onPreset: (getDate: () => Date) => void;
  onDateSelect: (date: Date | undefined) => void;
  onNoteChange: (note: string) => void;
  onSave: () => void;
  onCancel: () => void;
  canSave: boolean;
}

function SnoozeContent({
  selectedDate,
  note,
  onPreset,
  onDateSelect,
  onNoteChange,
  onSave,
  onCancel,
  canSave,
}: ISnoozeContentProps) {
  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs font-semibold text-foreground uppercase tracking-wider">Snooze Until</p>

      <div className="flex gap-2">
        {PRESETS.map(preset => (
          <Button
            key={preset.label}
            variant="outline"
            size="sm"
            className="flex-1 text-xs h-7"
            onClick={() => onPreset(preset.getDate)}
          >
            {preset.label}
          </Button>
        ))}
      </div>

      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground flex items-center gap-1">
          <CalendarIcon className="h-3.5 w-3.5" />
          Custom date
        </Label>
        <div className="border rounded-md overflow-hidden">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={onDateSelect}
            disabled={date => date <= new Date()}
            initialFocus
            className="p-2"
          />
        </div>
        {selectedDate && (
          <p className="text-xs text-muted-foreground">
            Selected: {format(selectedDate, 'dd MMM yyyy')}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor="snooze-note" className="text-xs text-muted-foreground">
          Note <span className="text-destructive">*</span>
        </Label>
        <Textarea
          id="snooze-note"
          value={note}
          onChange={e => onNoteChange(e.target.value)}
          placeholder="Reason for snoozing..."
          className="text-sm resize-none h-16"
          maxLength={300}
        />
        <p className="text-xs text-muted-foreground text-right">{note.length}/300</p>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="sm" onClick={onSave} disabled={!canSave}>
          Save
        </Button>
      </div>
    </div>
  );
}
